import { ENDPOINTS, STORAGE_KEYS } from '@/lib/api/endpoints';
import { ApiError, type ApiErrorHandler } from '@/lib/api/errors';
import type { Portal } from '@/types/api';

export type HttpMethod = 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE';

export interface ApiRequestOptions extends Omit<RequestInit, 'body'> {
  portal?: Portal | null;
  body?: unknown;
  formData?: FormData;
  skipAuth?: boolean;
}

let globalErrorHandler: ApiErrorHandler | undefined;

export function setApiErrorHandler(handler: ApiErrorHandler | undefined): void {
  globalErrorHandler = handler;
}

function resolvePortal(explicit?: Portal | null): Portal | null {
  if (explicit !== undefined) {
    return explicit;
  }
  const path = window.location.pathname;
  if (path.startsWith('/admin')) return 'admin';
  if (path.startsWith('/tenant')) return 'tenant';
  return null;
}

function getToken(portal: Portal | null): string | null {
  if (portal === 'admin') {
    return localStorage.getItem(STORAGE_KEYS.adminToken);
  }
  if (portal === 'tenant') {
    return localStorage.getItem(STORAGE_KEYS.tenantToken);
  }
  return null;
}

async function parseError(response: Response): Promise<ApiError> {
  let message = response.statusText || 'Request failed';
  let details: unknown;

  try {
    const data = (await response.json()) as { detail?: string | { msg?: string }[] | Record<string, unknown> };
    if (typeof data.detail === 'string') {
      message = data.detail;
    } else if (Array.isArray(data.detail)) {
      message = data.detail.map((item) => item.msg ?? JSON.stringify(item)).join(', ');
    } else if (data.detail && typeof data.detail === 'object') {
      const detailObj = data.detail as Record<string, unknown>;
      if (typeof detailObj.message === 'string') {
        const extra = typeof detailObj.error === 'string' ? `: ${detailObj.error}` : '';
        message = `${detailObj.message}${extra}`;
      } else {
        message = JSON.stringify(data.detail);
      }
    }
    details = data.detail;
  } catch {
    // ignore JSON parse errors
  }

  return new ApiError(message, response.status, String(response.status), details);
}

export async function apiRequest<T>(path: string, options: ApiRequestOptions = {}): Promise<T> {
  const portal = resolvePortal(options.portal);
  const headers = new Headers(options.headers);

  if (!options.formData && options.body !== undefined) {
    headers.set('Content-Type', 'application/json');
  }

  if (!options.skipAuth) {
    const token = getToken(portal);
    if (token) {
      headers.set('Authorization', `Bearer ${token}`);
    }
  }

  const response = await fetch(path, {
    ...options,
    headers,
    body: options.formData ?? (options.body !== undefined ? JSON.stringify(options.body) : undefined),
  });

  if (!response.ok) {
    const error = await parseError(response);
    globalErrorHandler?.(error, portal);
    throw error;
  }

  if (response.status === 204) {
    return undefined as T;
  }

  const contentType = response.headers.get('content-type') ?? '';
  if (contentType.includes('application/json')) {
    return (await response.json()) as T;
  }

  return (await response.text()) as T;
}

export const apiClient = {
  get: <T>(path: string, options?: ApiRequestOptions) =>
    apiRequest<T>(path, { ...options, method: 'GET' }),
  post: <T>(path: string, body?: unknown, options?: ApiRequestOptions) =>
    apiRequest<T>(path, { ...options, method: 'POST', body }),
  put: <T>(path: string, body?: unknown, options?: ApiRequestOptions) =>
    apiRequest<T>(path, { ...options, method: 'PUT', body }),
  patch: <T>(path: string, body?: unknown, options?: ApiRequestOptions) =>
    apiRequest<T>(path, { ...options, method: 'PATCH', body }),
  delete: <T>(path: string, options?: ApiRequestOptions) =>
    apiRequest<T>(path, { ...options, method: 'DELETE' }),
  postForm: <T>(path: string, formData: FormData, options?: ApiRequestOptions) =>
    apiRequest<T>(path, { ...options, method: 'POST', formData }),
};

export { ENDPOINTS, STORAGE_KEYS };
